import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, KeyRound } from "lucide-react";
import api, { formatDetail } from "@/lib/api";

export default function ManageUsers() {
  const [users, setUsers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetting, setResetting] = useState(null);
  const load = async () => { const { data } = await api.get("/admin/users"); setUsers(data); };
  useEffect(() => { load(); }, []);
  const create = async (form) => { try { await api.post("/admin/users", { ...form, role: "receptionist" }); toast.success("Created"); setCreateOpen(false); load(); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); } };
  const toggleActive = async (u) => { try { await api.patch(`/admin/users/${u.id}`, { active: !u.active }); load(); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); } };
  const resetPwd = async (newP) => { try { await api.patch(`/admin/users/${resetting.id}`, { password: newP }); toast.success("Reset"); setResetting(null); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); } };

  return (
    <AdminLayout title="User Accounts" showTurnBoard={false}>
      <div className="flex justify-end mb-6">
        <Button onClick={() => setCreateOpen(true)} className="rounded-full bg-zen-primary text-white" data-testid="new-receptionist-btn"><Plus className="w-4 h-4 mr-1" /> Add Receptionist</Button>
      </div>
      <div className="bg-white rounded-2xl border border-zen-border overflow-hidden">
        <table className="w-full">
          <thead className="bg-zen-surface text-xs uppercase text-zen-muted">
            <tr><th className="text-left px-5 py-3">User</th><th className="text-left px-5 py-3">Role</th><th className="text-left px-5 py-3">Active</th><th className="text-right px-5 py-3">Actions</th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-zen-border" data-testid={`user-row-${u.id}`}>
                <td className="px-5 py-4"><div className="font-medium">{u.full_name}</div><div className="text-xs text-zen-muted">@{u.username}</div></td>
                <td className="px-5 py-4 capitalize">{u.role}</td>
                <td className="px-5 py-4"><Switch checked={u.active} onCheckedChange={() => toggleActive(u)} disabled={u.role === "owner"} data-testid={`toggle-active-${u.id}`} /></td>
                <td className="px-5 py-4 text-right"><Button size="sm" variant="outline" onClick={() => setResetting(u)} className="rounded-full" data-testid={`reset-pwd-${u.id}`}><KeyRound className="w-3 h-3 mr-1" /> Reset</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CreateRecep open={createOpen} onClose={() => setCreateOpen(false)} onSave={create} />
      <Dialog open={!!resetting} onOpenChange={(v) => !v && setResetting(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset Password</DialogTitle></DialogHeader>
          <PwdForm onSave={resetPwd} />
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function CreateRecep({ open, onClose, onSave }) {
  const [form, setForm] = useState({ username: "", password: "", full_name: "" });
  useEffect(() => { if (open) setForm({ username: "", password: "", full_name: "" }); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Add Receptionist</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="bg-zen-surface border-0" data-testid="recep-username" />
          <Input type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-zen-surface border-0" data-testid="recep-password" />
          <Input placeholder="Full Name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="bg-zen-surface border-0" data-testid="recep-name" />
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={() => onSave(form)} className="bg-zen-primary text-white" data-testid="recep-save">Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PwdForm({ onSave }) {
  const [pwd, setPwd] = useState("");
  return (
    <>
      <Input type="password" placeholder="New password" value={pwd} onChange={(e) => setPwd(e.target.value)} className="bg-zen-surface border-0" data-testid="reset-new-password" />
      <DialogFooter><Button onClick={() => onSave(pwd)} disabled={!pwd} className="bg-zen-primary text-white" data-testid="reset-save">Reset</Button></DialogFooter>
    </>
  );
}
